import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays, Users, Building2, QrCode, Eye, CheckCircle, XCircle,
  Clock, Pause, Play, ChevronRight, Search, Mail, RefreshCw, Edit, Shield, Send
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  new_request: { label: "Nouvelle demande", color: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  reviewing: { label: "En cours d'etude", color: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20" },
  approved: { label: "Approuvee", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  activated: { label: "Active", color: "bg-green-500/10 text-green-400 border-green-500/20" },
  rejected: { label: "Refusee", color: "bg-red-500/10 text-red-400 border-red-500/20" },
  suspended: { label: "Suspendue", color: "bg-orange-500/10 text-orange-400 border-orange-500/20" },
};

export default function EventCenterPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };
  const [dashboard, setDashboard] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, orgsRes] = await Promise.all([
        axios.get(`${API}/api/event/admin/dashboard`, { headers }),
        axios.get(`${API}/api/event/admin/organizations`, { headers }),
      ]);
      setDashboard(dashRes.data);
      setOrganizations(orgsRes.data);
    } catch (e) {
      toast.error("Erreur chargement Event Center");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = organizations.filter(o =>
    !search || o.org_name?.toLowerCase().includes(search.toLowerCase()) || o.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-[#0099CC] border-t-transparent rounded-full" /></div>;

  const d = dashboard || { organizations: {}, events: {}, participants: {}, scans: {} };

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="event-center-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <CalendarDays className="w-7 h-7 text-[#0099CC]" /> Event Center
          </h1>
          <p className="text-sm text-[#94A3B8] mt-1">Pilotage de la plateforme evenementielle</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Organisations", value: d.organizations.total, icon: Building2, color: "#0099CC", sub: `${d.organizations.active} actives` },
          { label: "Evenements", value: d.events.total, icon: CalendarDays, color: "#10B981", sub: `${d.events.published} publies` },
          { label: "Participants", value: d.participants.total, icon: Users, color: "#8B5CF6", sub: `${d.participants.checked_in} presents` },
          { label: "Demandes", value: d.organizations.pending, icon: Clock, color: "#F59E0B", sub: `${d.organizations.suspended} suspendues` },
        ].map((kpi, i) => (
          <div key={i} className="bg-[#0B1120] border border-[#1A2332] rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-[#64748B] uppercase tracking-wider">{kpi.label}</span>
              <kpi.icon className="w-4 h-4" style={{ color: kpi.color }} />
            </div>
            <div className="text-2xl font-bold text-white">{kpi.value || 0}</div>
            <div className="text-xs mt-1" style={{ color: kpi.color }}>{kpi.sub}</div>
          </div>
        ))}
      </div>

      {/* Organizations list */}
      <div className="bg-[#0B1120] border border-[#1A2332] rounded-xl">
        <div className="p-4 border-b border-[#1A2332] flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <h2 className="text-lg font-semibold text-white">Organisations</h2>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-[#64748B]" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..." className="pl-9 bg-[#060B14] border-[#1A2332] text-white h-9" data-testid="org-search" />
          </div>
        </div>

        <div className="divide-y divide-[#1A2332]">
          {filtered.length === 0 && (
            <div className="p-8 text-center text-[#64748B]">Aucune organisation trouvee</div>
          )}
          {filtered.map(org => {
            const st = STATUS_MAP[org.status] || STATUS_MAP.new_request;
            return (
              <div key={org.id} className="p-4 hover:bg-[#0D1424] transition-colors cursor-pointer flex items-center justify-between" onClick={() => navigate(`/app/event-center/org/${org.id}`)} data-testid={`org-row-${org.id}`}>
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: (org.primary_color || "#0099CC") + "20" }}>
                    <Building2 className="w-5 h-5" style={{ color: org.primary_color || "#0099CC" }} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-white truncate">{org.org_name}</div>
                    <div className="text-xs text-[#64748B] truncate">{org.email} — {org.city}, {org.country}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`text-xs px-2.5 py-1 rounded-full border ${st.color}`}>{st.label}</span>
                  <ChevronRight className="w-4 h-4 text-[#64748B]" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
